import React, { useState, useEffect, useRef } from 'react';
import useIsMobile from '../utils/useIsMobile';

// Infinite (seamless-loop) equal-size carousel for single photos.
// 양끝 빈 공간 없이 무한 회전 — 첫 사진 왼쪽에 끝 사진이 이어진다(2026-06-17).
//
// 방식: 이미지 배열을 3벌 복제해 한 줄에 깔고(가운데 벌에서 시작), translateX로
// 활성 카드를 중앙에 둔다. 좌우에 항상 이웃 카드가 있어 양끝이 비지 않는다.
// 활성 카드가 마지막 벌 경계에 닿으면 transition 없이(=무전환) 가운데 벌의
// 동일 위치로 스냅 → 내용이 주기적이라 픽셀 단위로 동일해 끊김이 보이지 않는다.
//
// 원통형 느낌: 양옆 카드에 perspective+rotateY+축소를 적용(중앙은 정면·원본 크기).
// flex-basis(레이아웃 너비)는 모든 카드 동일하게 고정하고 transform만 쓰므로,
// 과거 센터-스테이지가 겪던 '들썩임'(폭 애니메이션)이 재발하지 않는다.
//
// Layout math (CARD_W = 트랙 폭 대비 %, 모든 카드 동일):
//   활성 카드(가상 위치 pos)의 좌측 모서리는 pos*CARD_W → 중앙(50%)에 오도록
//   offset = 50 - pos*CARD_W - CARD_W/2 만큼 트랙을 이동.
//
// Props:
//   images: [{src, alt}]
//   intervalMs: 자동 전환 간격 (기본 3000)
//   aspectRatio: 각 카드 CSS aspect-ratio (기본 '4 / 3')
//   onSelect: (logicalIndex) => void — 중앙 카드 클릭 시 호출 (예: 모달 열기)
//   jumpToRef: ref. (logicalIndex) => void 를 받아 캐러셀을 명령형으로 이동
//              (그리드 모달에서 고른 사진으로 점프)
//   renderItem: (item, {active}) => JSX — opt-in 카드 내부 커스텀 렌더.
//              없으면 기본 단일 img를 렌더(고객 캐러셀 등 기존 소비처 무영향).
//              비포/애프터 좌/우 페어처럼 1장 이상을 한 카드에 담을 때 사용.

// 중앙 카드를 작업 갤러리(maxWidth 960 풀폭)와 동일감으로 키움(2026-06-29).
// 컨테이너 1160 × 84% ≈ 950px 중앙 + 양옆은 살짝만 보이는 코버플로우 유지.
const CARD_W_DESKTOP = 84; // 카드 폭, 트랙 대비 % (데스크톱)
const CARD_W_MOBILE = 80;  // 모바일은 넓게 — 중앙 사진 강조 유지
const SIDE_ROTATE_DEG = 16; // 양옆 카드 회전각(원통형 느낌). 0이면 평면
const SIDE_SCALE = 0.9;     // 양옆 카드 축소율(깊이감)

// 가상화 창(window): 활성 슬롯 pos 기준 ±WINDOW 안의 슬롯만 실제 이미지/버튼을
// 렌더하고, 밖은 동일 크기의 빈 자리표시 div만 둔다(레이아웃 수식·translateX·스냅
// 불변, 지오메트리 회귀 0). 무한 루프가 전체를 3벌 복제해 <img> 수백 개가 동시에
// DOM에 올라가 저사양 폰이 버벅이던 문제 완화(2026-07-01). 한 칸씩 이동 + 0.6s
// 전환 중에도 들어오는 카드가 이미 렌더돼 있도록 여유를 둔다.
const WINDOW = 3;

const mod = (n, m) => ((n % m) + m) % m;
const clamp1 = (n) => Math.max(-1, Math.min(1, n));

export default function CoverflowCarousel({
  images,
  intervalMs = 3000,
  aspectRatio = '4 / 3',
  onSelect,
  jumpToRef,
  renderItem,
}) {
  const total = images?.length || 0;
  const looped = total > 1; // 2장 이상일 때만 무한 루프(복제) 동작
  const isMobile = useIsMobile();
  const CARD_W = isMobile ? CARD_W_MOBILE : CARD_W_DESKTOP;

  // 가상 위치. 루프 시 가운데 벌의 첫 사진(=total)에서 시작.
  const startPos = looped ? total : 0;
  const [pos, setPos] = useState(startPos);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // 이미지 로드/개수 변경 시 시작 위치로 리셋.
  useEffect(() => {
    setAnimate(true);
    setPos(looped ? total : 0);
  }, [total, looped]);

  // 그리드 모달이 고른 사진으로 캐러셀을 이동시키는 명령형 점프.
  // 현재 벌(copy) 안에서 해당 논리 인덱스로 — 이동 거리 최소화.
  useEffect(() => {
    if (!jumpToRef) return;
    jumpToRef.current = (i) => {
      if (i < 0 || i >= total) return;
      setAnimate(true);
      setPos(p => (p - mod(p, total)) + i);
    };
  }, [jumpToRef, total]);

  // 자동 회전 (hover 시 일시정지).
  useEffect(() => {
    if (total <= 1 || paused) return undefined;
    timerRef.current = setInterval(() => {
      setAnimate(true);
      setPos(p => p + 1);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [total, paused, intervalMs]);

  // 무전환 스냅 후 다음 프레임에 transition 복원 (pos 불변이라 움직이지 않음).
  useEffect(() => {
    if (animate) return undefined;
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, [animate]);

  if (total === 0) return null;

  const go = (delta) => { setAnimate(true); setPos(p => p + delta); };
  const goToLogical = (i) => { setAnimate(true); setPos(p => (p - mod(p, total)) + i); };

  // 끝 벌 경계에 닿으면 가운데 벌의 동일 지점으로 무전환 스냅.
  const onTrackTransitionEnd = (e) => {
    if (!looped || e.target !== e.currentTarget) return;
    if (pos >= 2 * total) { setAnimate(false); setPos(pos - total); }
    else if (pos < total) { setAnimate(false); setPos(pos + total); }
  };

  // 렌더할 카드: 루프면 3벌 복제, 아니면 1벌.
  const copies = looped ? 3 : 1;
  const cards = [];
  for (let c = 0; c < copies; c++) {
    for (let i = 0; i < total; i++) {
      cards.push({ img: images[i], logical: i, p: c * total + i });
    }
  }

  const offset = 50 - pos * CARD_W - CARD_W / 2;
  const activeLogical = mod(pos, total);

  return (
    <div
      style={{ position: 'relative', width: '100%', overflow: 'hidden' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        onTransitionEnd={onTrackTransitionEnd}
        style={{
          display: 'flex',
          alignItems: 'center',
          transform: `translateX(${offset}%)`,
          transition: animate
            ? 'transform 0.6s cubic-bezier(0.22, 0.61, 0.36, 1)'
            : 'none',
        }}
      >
        {cards.map(({ img, logical, p }) => {
          // 창 밖 슬롯: 동일 flexBasis/padding의 빈 자리표시만(레이아웃 유지, 콘텐츠 0).
          if (Math.abs(p - pos) > WINDOW) {
            return (
              <div
                key={`${img.id || img.src}-${p}`}
                aria-hidden="true"
                style={{
                  flexGrow: 0, flexShrink: 0,
                  flexBasis: `${CARD_W}%`,
                  padding: '0 6px',
                  boxSizing: 'border-box',
                }}
              />
            );
          }
          const active = p === pos;
          const d = clamp1(p - pos); // 중앙 기준 좌(-1)/우(+1) 이웃
          // 원통형: 양옆 카드는 안쪽으로 회전 + 축소, 중앙은 정면·원본 크기.
          const ry = -d * SIDE_ROTATE_DEG;
          const sc = active ? 1 : SIDE_SCALE;
          return (
            <div
              key={`${img.id || img.src}-${p}`}
              style={{
                // 모든 카드 동일 폭 — translateX만 움직이고 레이아웃 폭은 불변(들썩임 방지).
                flexGrow: 0, flexShrink: 0,
                flexBasis: `${CARD_W}%`,
                padding: '0 6px',
                boxSizing: 'border-box',
                opacity: active ? 1 : 0.5,
                transition: animate ? 'opacity 0.6s ease' : 'none',
              }}
            >
              <button
                type="button"
                onClick={() => (active ? onSelect && onSelect(logical) : setPos(p))}
                aria-label={active ? `${img.alt || '사진'} 크게 보기` : `${logical + 1}번 사진으로 이동`}
                style={{
                  display: 'block', width: '100%', aspectRatio,
                  padding: 0, border: 'none', borderRadius: 14, overflow: 'hidden',
                  background: 'var(--surface)',
                  cursor: 'pointer',
                  transform: `perspective(1200px) rotateY(${ry}deg) scale(${sc})`,
                  transformOrigin: 'center center',
                  transition: animate ? 'transform 0.6s cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none',
                  boxShadow: active
                    ? '0 12px 32px rgba(0,0,0,0.45)'
                    : '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                {renderItem ? renderItem(img, { active }) : (
                  <img
                    src={img.src}
                    alt={img.alt || ''}
                    loading={p < total ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover', display: 'block',
                    }}
                  />
                )}
              </button>
            </div>
          );
        })}
      </div>

      {total > 1 && (
        <>
          <ArrowButton dir="left" onClick={() => go(-1)} />
          <ArrowButton dir="right" onClick={() => go(1)} />
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14,
          }}>
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goToLogical(i)}
                aria-label={`${i + 1}번 사진`}
                style={{
                  width: i === activeLogical ? 18 : 8, height: 8, borderRadius: 4,
                  padding: 0, border: 'none', cursor: 'pointer',
                  background: i === activeLogical ? 'var(--primary)' : 'rgba(255,255,255,0.3)',
                  transition: 'width 0.3s, background 0.3s',
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ArrowButton({ dir, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label={dir === 'left' ? '이전' : '다음'}
      style={{
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        [dir]: 10, zIndex: 2,
        width: 42, height: 42, borderRadius: '50%',
        background: 'rgba(0,0,0,0.5)', color: '#fff',
        border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.5rem', lineHeight: 1,
      }}
    >
      {dir === 'left' ? '‹' : '›'}
    </button>
  );
}
